import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
} from 'framer-motion';
import {
  ArrowRightOnRectangleIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  MegaphoneIcon,
  PaintBrushIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { IUser, UserRole } from '../../types/auth.types';
import {
  getUserDisplayName,
  getUserFullName,
  getUserInitials,
} from '../../utils/userHelpers';
import { MobileAccountIdentity } from './MobileAccountIdentity';
import {
  buildUserNavMenuConfigForPersona,
  type NavPersona,
  PERSONA_ACCENT_STYLES,
  type UserNavMenuItem,
} from '../../config/userNavMenu';
import { type WorkspaceId } from '../../config/workspaces';
import { CREATOR_APPLY_PATH, CREATOR_ONBOARDING_PATH } from '../../config/creatorProgramPaths';
import { SELLER_APPLY_PATH } from '../../config/sellerProgramPaths';
import { useWorkspaceSelection } from '../../hooks/useWorkspaceSelection';
import { creatorService } from '../../services/api/creator.service';
import { hasCreatorPortalRole } from '../../utils/creatorPortalAccess';
import { getRoutePrefetchHandlers } from '../../utils/routePrefetch';
import {
  userNavItemVariants,
  userNavListVariants,
  userNavTransition,
} from './userNavMotion';
import { WorkspacesSwitcher } from './WorkspacesSwitcher';

type DiscoveryVariant = 'creator' | 'seller';

type DiscoveryNavItem = UserNavMenuItem & {
  discoveryVariant: DiscoveryVariant;
};

const DISCOVERY_CARD_STYLES: Record<
  DiscoveryVariant,
  {
    row: string;
    icon: string;
    title: string;
    description: string;
    chevron: string;
  }
> = {
  creator: {
    row: 'border-fuchsia-200/90 bg-gradient-to-r from-fuchsia-50/95 via-white to-white hover:border-fuchsia-300 hover:from-fuchsia-50',
    icon: 'bg-gradient-to-br from-fuchsia-600 to-pink-600 text-white shadow-sm shadow-fuchsia-200/50',
    title: 'text-fuchsia-950',
    description: 'text-fuchsia-700/75',
    chevron: 'text-fuchsia-300 group-hover:text-fuchsia-500',
  },
  seller: {
    row: 'border-violet-200/90 bg-gradient-to-r from-violet-50/95 via-white to-white hover:border-violet-300 hover:from-violet-50',
    icon: 'bg-gradient-to-br from-violet-600 to-purple-600 text-white shadow-sm shadow-violet-200/50',
    title: 'text-violet-950',
    description: 'text-violet-700/75',
    chevron: 'text-violet-300 group-hover:text-violet-500',
  },
};

type UserNavMenuPanelProps = {
  user: IUser;
  variant: 'dropdown' | 'sheet';
  onNavigate: () => void;
  onLogout: () => void;
  /** When true, skip the sheet hero (rendered by AccountMobileSheet). */
  hideIdentityHeader?: boolean;
};

function workspaceToPersona(workspaceId: WorkspaceId): NavPersona {
  if (workspaceId === 'seller') return 'seller';
  if (workspaceId === 'creator') return 'creator';
  if (workspaceId === 'owner') return 'owner';
  if (workspaceId === 'staff') return 'staff';
  return 'buyer';
}

export function UserNavMenuPanel({
  user,
  variant,
  onNavigate,
  onLogout,
  hideIdentityHeader = false,
}: UserNavMenuPanelProps) {
  const location = useLocation();
  const isSheet = variant === 'sheet';
  const {
    selectedWorkspaceId,
    handleWorkspaceSelect,
    hasMultipleWorkspaces,
    onboardingComplete,
    onboardingLoading,
  } = useWorkspaceSelection(user);
  const [creatorCtaLabel, setCreatorCtaLabel] = useState('Apply as Creator');
  const isCreator = hasCreatorPortalRole(user.roles);
  const isSeller = user.roles.includes(UserRole.SELLER);

  useEffect(() => {
    if (isCreator) {
      setCreatorCtaLabel(onboardingComplete === false ? 'Continue setup' : 'Creator Studio');
      return;
    }
    let mounted = true;
    creatorService
      .getMyApplication()
      .then((ctx) => {
        if (!mounted) return;
        if (ctx.application?.status === 'PENDING') {
          setCreatorCtaLabel('Application Status');
        } else if (ctx.application?.status === 'REJECTED') {
          setCreatorCtaLabel('View decision & re-apply');
        } else {
          setCreatorCtaLabel('Apply as Creator');
        }
      })
      .catch(() => {
        if (mounted) setCreatorCtaLabel('Apply as Creator');
      });
    return () => {
      mounted = false;
    };
  }, [isCreator, onboardingComplete]);

  const selectedPersona = workspaceToPersona(selectedWorkspaceId);
  const menu = useMemo(() => {
    const base = buildUserNavMenuConfigForPersona(user, selectedPersona);
    if (selectedPersona === 'creator' && onboardingComplete !== true) {
      if (onboardingLoading) {
        return {
          ...base,
          hubPath: CREATOR_ONBOARDING_PATH,
          hubDescription: 'Loading your creator workspace…',
          items: [],
        };
      }
      return {
        ...base,
        hubPath: CREATOR_ONBOARDING_PATH,
        hubDescription: 'Complete setup to unlock dashboard, links & earnings',
        items: [
          {
            label: 'Continue setup',
            description: 'Niches, storefront & referral links',
            path: CREATOR_ONBOARDING_PATH,
            icon: SparklesIcon,
          },
        ],
      };
    }
    return base;
  }, [user, selectedPersona, onboardingComplete, onboardingLoading]);
  const accent = PERSONA_ACCENT_STYLES[menu.accent];

  const discoveryItems = useMemo((): DiscoveryNavItem[] => {
    const items: DiscoveryNavItem[] = [];
    if (!isCreator) {
      items.push({
        label: creatorCtaLabel,
        description: 'Promote products and earn commission',
        path: CREATOR_APPLY_PATH,
        icon: MegaphoneIcon,
        discoveryVariant: 'creator',
      });
    }
    if (!isSeller) {
      items.push({
        label: 'Sell on Qumashi',
        description: 'Apply to launch your storefront',
        path: SELLER_APPLY_PATH,
        icon: PaintBrushIcon,
        discoveryVariant: 'seller',
      });
    }
    return items;
  }, [creatorCtaLabel, isCreator, isSeller]);

  const listItems = menu.items;

  const isPathActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const renderNavLink = (item: UserNavMenuItem) => {
    const active = isPathActive(item.path);
    const Icon = item.icon;
    return (
      <motion.div key={item.path} variants={userNavItemVariants}>
        <Link
          to={item.path}
          onClick={onNavigate}
          {...getRoutePrefetchHandlers(item.path)}
          className={`group relative flex items-center gap-3 rounded-xl px-2.5 transition active:scale-[0.99] ${
            active ? accent.rowActive : accent.rowHover
          } ${isSheet ? 'py-2.5' : 'py-2'}`}
        >
          {active ? (
            <span
              aria-hidden
              className={`absolute bottom-2 left-0 top-2 w-1 rounded-full bg-gradient-to-b ${accent.hub}`}
            />
          ) : null}
          <div
            className={`flex shrink-0 items-center justify-center rounded-lg transition ${
              active ? accent.iconActive : accent.icon
            } ${isSheet ? 'h-9 w-9' : 'h-7 w-7'}`}
          >
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p
              className={`truncate text-sm font-medium ${
                active ? accent.textActive : 'text-slate-900'
              }`}
            >
              {item.label}
            </p>
            {isSheet ? (
              <p className="truncate text-xs text-slate-500">{item.description}</p>
            ) : null}
          </div>
          <ChevronRightIcon
            className={`h-3.5 w-3.5 shrink-0 transition group-hover:translate-x-0.5 ${accent.chevron} ${accent.chevronHover}`}
          />
        </Link>
      </motion.div>
    );
  };

  const renderDiscoveryLink = (item: DiscoveryNavItem) => {
    const active = isPathActive(item.path);
    const Icon = item.icon;
    const styles = DISCOVERY_CARD_STYLES[item.discoveryVariant];
    return (
      <motion.div key={item.path} variants={userNavItemVariants}>
        <Link
          to={item.path}
          onClick={onNavigate}
          {...getRoutePrefetchHandlers(item.path)}
          className={`group flex items-center gap-3 rounded-xl border px-2.5 transition active:scale-[0.99] ${styles.row} ${
            isSheet ? 'py-2.5' : 'py-2'
          } ${active ? 'ring-1 ring-slate-300/80' : ''}`}
        >
          <div
            className={`flex shrink-0 items-center justify-center rounded-lg ${styles.icon} ${
              isSheet ? 'h-9 w-9' : 'h-7 w-7'
            }`}
          >
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className={`truncate text-sm font-semibold ${styles.title}`}>{item.label}</p>
            <p className={`truncate text-xs ${styles.description}`}>{item.description}</p>
          </div>
          <ChevronRightIcon
            className={`h-3.5 w-3.5 shrink-0 transition group-hover:translate-x-0.5 ${styles.chevron}`}
          />
        </Link>
      </motion.div>
    );
  };

  return (
    <div
      className={
        isSheet ? 'flex min-h-0 flex-1 flex-col bg-white' : ''
      }
    >
      {isSheet && !hideIdentityHeader ? (
        <MobileAccountIdentity user={user} variant="hero" />
      ) : !isSheet ? (
        <div className={`border-b bg-white px-3 py-3 ${accent.panelBorder}`}>
          <div className="flex items-center gap-3">
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-sm font-semibold text-white ring-2 ring-white ${accent.avatarGradient}`}
            >
              {getUserInitials(user)}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-sm font-semibold text-slate-900">
                {getUserDisplayName(user)}
              </p>
              <p className="mt-0.5 truncate text-xs text-slate-500">
                {user.email}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${accent.badge}`}
              >
                {menu.personaLabel}
              </span>
              <p className="mt-1 max-w-[7rem] truncate text-[11px] text-slate-500">
                {menu.hubLabel}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <div
        className={`${
          isSheet
            ? 'flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain'
            : ''
        } p-2`}
      >
        {hasMultipleWorkspaces ? (
          <WorkspacesSwitcher
            user={user}
            selectedWorkspaceId={selectedWorkspaceId}
            onWorkspaceSelect={handleWorkspaceSelect}
            creatorOnboardingComplete={onboardingComplete}
          />
        ) : null}

        {listItems.length > 0 ? (
          <motion.div
            className={`${isSheet ? 'mt-2' : hasMultipleWorkspaces ? 'mt-1 px-1' : 'mt-1 px-1'} space-y-0.5`}
            variants={userNavListVariants}
            initial="initial"
            animate="animate"
            key={selectedWorkspaceId}
          >
            {listItems.map((item) => renderNavLink(item))}
          </motion.div>
        ) : null}

        {discoveryItems.length > 0 ? (
          <motion.div
            className={`${isSheet ? 'mt-3' : 'mt-2 px-1'} space-y-1.5 border-t border-slate-100 pt-2.5`}
            variants={userNavListVariants}
            initial="initial"
            animate="animate"
          >
            <p className="px-1 pb-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Grow on Qumashi
            </p>
            {discoveryItems.map((item) => renderDiscoveryLink(item))}
          </motion.div>
        ) : null}
      </div>

      <div
        className={`border-t px-2 py-2 ${accent.panelBorder} ${
          isSheet ? 'shrink-0 bg-white pb-[max(0.75rem,env(safe-area-inset-bottom))]' : accent.panelFooter
        }`}
      >
        <Link
          to={menu.profilePath}
          onClick={onNavigate}
          {...getRoutePrefetchHandlers(menu.profilePath)}
          className={`group flex items-center gap-3 rounded-xl px-2.5 transition active:scale-[0.99] ${accent.rowHover} ${
            isSheet ? 'py-2.5' : 'py-2'
          }`}
        >
          <div
            className={`flex shrink-0 items-center justify-center rounded-lg transition ${accent.profileIcon} ${accent.profileIconHover} ${
              isSheet ? 'h-9 w-9' : 'h-7 w-7'
            }`}
          >
            <Cog6ToothIcon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-900">
              {menu.profileLabel}
            </p>
            {isSheet ? (
              <p className="truncate text-xs text-slate-500">
                {menu.profileDescription}
              </p>
            ) : null}
          </div>
        </Link>

        <button
          type="button"
          onClick={onLogout}
          className={`group flex w-full items-center gap-3 rounded-xl px-2.5 text-left transition hover:bg-red-50 active:scale-[0.99] ${
            isSheet ? 'py-2.5' : 'py-2'
          }`}
        >
          <div
            className={`flex shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition group-hover:bg-red-100 group-hover:text-red-600 ${
              isSheet ? 'h-9 w-9' : 'h-7 w-7'
            }`}
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900 group-hover:text-red-600">
              Sign out
            </p>
            {isSheet ? (
              <p className="text-xs text-slate-500">
                {getUserFullName(user)} · end session
              </p>
            ) : null}
          </div>
        </button>
      </div>
    </div>
  );
}

export function UserNavMenuPanelAnimated({
  isVisible,
  ...props
}: UserNavMenuPanelProps & { isVisible: boolean }) {
  const reduceMotion = useReducedMotion();

  return (
    <AnimatePresence mode="wait">
      {isVisible ? (
        <motion.div
          key="user-nav-panel"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={userNavTransition(reduceMotion)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <UserNavMenuPanel {...props} />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
